import { Alert, Checkbox, DatePicker, Input, Modal } from "antd";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useMemo, useState } from "react";
import * as Yup from "yup";
import { useCreateSavingsGoal } from "../../api/api";
import { useGetGroupUsers2 } from "../../api/api.get";
import { useAuthContext } from "../../providers/auth.provider";
import { useCurrencyContext } from "../../providers/currency.provider";
import { useGroupContext } from "../../providers/groups.provider";
import { capitalize } from "../../utils/functions";

export default function CreateSavingsGoalDialog({
  open,
  onCancel,
  onSuccess,
}: {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}) {
  const { user } = useAuthContext();
  const { defaultGroupId } = useGroupContext();
  const { currency } = useCurrencyContext();
  const { mutateAsync, error, isPending } =
    useCreateSavingsGoal(defaultGroupId);

  const { data: groupUsers } = useGetGroupUsers2(defaultGroupId);

  const [selectedUsernames, setSelectedUsernames] = useState<string[]>([]);

  const formik = useFormik({
    initialValues: {
      name: "",
      targetAmount: "",
      targetDate: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please enter a goal name"),
      targetAmount: Yup.number()
        .typeError("Please enter a valid number")
        .positive("Amount must be greater than zero")
        .required("Please enter a target amount"),
      targetDate: Yup.string().required("Please select a target date"),
    }),
    onSubmit: async (values) => {
      mutateAsync({
        name: values.name,
        targetAmount: +values.targetAmount,
        targetDate: values.targetDate,
        users: selectedUsernames,
      }).then(() => {
        onSuccess();
      });
    },
  });

  const userOptions = useMemo(() => {
    if (!groupUsers) return [];
    return groupUsers
      .filter((gu) => gu.username !== user?.username)
      .map((gu) => ({
        label: capitalize(gu.firstName || gu.username),
        value: gu.username,
      }));
  }, [groupUsers, user]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Modal
        title="Create Savings Goal"
        open={open}
        onOk={formik.submitForm}
        onCancel={onCancel}
        okText="Create"
        okButtonProps={{ loading: isPending }}
      >
        <div className="flex flex-col gap-4 my-6">
          {error && <Alert type="error" message="Whoops! Please try again." />}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Goal Name *
            </label>
            <Input
              placeholder="e.g., Emergency Fund, New Car"
              {...formik.getFieldProps("name")}
              status={formik.errors.name ? "error" : undefined}
            />
            {formik.errors.name && (
              <p className="text-xs text-red-500 mt-1">{formik.errors.name}</p>
            )}
          </div>

          {/* Target amount */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Target Amount *
            </label>
            <Input
              type="number"
              prefix={currency}
              placeholder="e.g., 10000"
              {...formik.getFieldProps("targetAmount")}
              status={formik.errors.targetAmount ? "error" : undefined}
            />
            {formik.errors.targetAmount && (
              <p className="text-xs text-red-500 mt-1">
                {formik.errors.targetAmount}
              </p>
            )}
          </div>

          {/* Target date */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Target Date *
            </label>
            <DatePicker
              className="w-full"
              disabledDate={(current) =>
                current && current < dayjs().endOf("day")
              }
              onChange={(date) =>
                formik.setFieldValue("targetDate", date?.format("YYYY-MM-DD"))
              }
              status={formik.errors.targetDate ? "error" : undefined}
            />
            {formik.errors.targetDate && (
              <p className="text-xs text-red-500 mt-1">
                {formik.errors.targetDate}
              </p>
            )}
          </div>

          {/* Share with */}
          <div>
            <p className="text-sm font-semibold mb-2">Share With?</p>
            {userOptions.length > 0 ? (
              <Checkbox.Group
                options={userOptions}
                value={selectedUsernames}
                onChange={(values: string[]) => setSelectedUsernames(values)}
              />
            ) : (
              <p className="text-xs text-muted-foreground">
                No other members in this group to share with.
              </p>
            )}
          </div>
        </div>
      </Modal>
    </form>
  );
}
